'use client'

import { ChevronLeft, ChevronRight, X } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'

import {
  type CollapsedSide,
  DEFAULT_RATIO,
  MAX_RATIO,
  MIN_RATIO,
  RATIO_STEP,
  SNAP_RATIO,
  clampRatio,
  useCollapsiblePair,
} from './useCollapsiblePair'
import { cn } from '../lib/cn'

// ── Handle ───────────────────────────────────────────────────────────────────

const chevronButton =
  'flex cursor-pointer items-center justify-center rounded-md p-0.5 text-muted-foreground transition-all hover:bg-input hover:text-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring active:scale-95'

interface CollapseHandleProps {
  collapsed: CollapsedSide
  onCollapse: (side: 'left' | 'right') => void
  onExpand: () => void
}

const CollapseHandle = ({ collapsed, onCollapse, onExpand }: CollapseHandleProps) => {
  if (collapsed) {
    const ExpandIcon = collapsed === 'left' ? ChevronRight : ChevronLeft

    return (
      <button type='button' onClick={onExpand} aria-label={`Expand ${collapsed} panel`} className={chevronButton}>
        <ExpandIcon className='size-4' />
      </button>
    )
  }

  return (
    <div className='flex items-center gap-0 transition-all group-hover:gap-2'>
      <button
        type='button'
        onClick={() => onCollapse('left')}
        aria-label='Collapse left panel'
        className={chevronButton}
      >
        <ChevronLeft className='size-4' />
      </button>
      <button
        type='button'
        onClick={() => onCollapse('right')}
        aria-label='Collapse right panel'
        className={cn(chevronButton, '-ml-1.5 group-hover:ml-0')}
      >
        <ChevronRight className='size-4' />
      </button>
    </div>
  )
}

// ── Layout ───────────────────────────────────────────────────────────────────

interface DualCollapsibleProps {
  left: React.ReactNode
  right: React.ReactNode
  defaultCollapsedSide?: CollapsedSide
  className?: string
  leftClassName?: string
  rightClassName?: string
  localStorageKey?: string
}

export const DualCollapsible = ({
  left,
  right,
  defaultCollapsedSide = null,
  className,
  leftClassName,
  rightClassName,
  localStorageKey = 'dual-collapsible-state',
}: DualCollapsibleProps) => {
  const { collapsed, ratio, setRatio, commitRatio, collapse, expand, hintSeen, dismissHint } = useCollapsiblePair(
    localStorageKey,
    defaultCollapsedSide,
  )

  const containerRef = useRef<HTMLDivElement>(null)
  const [dragging, setDragging] = useState(false)
  const draggable = !collapsed

  // Show the one-time "drag to resize" hint until any resize/collapse gesture.
  const showHint = hintSeen === false && draggable && !dragging

  // While dragging, lock the cursor + suppress text selection globally so the
  // pointer can leave the thin handle without losing the resize affordance.
  useEffect(() => {
    if (!dragging) return

    const { body } = document
    const prevCursor = body.style.cursor
    const prevSelect = body.style.userSelect
    body.style.cursor = 'col-resize'
    body.style.userSelect = 'none'

    return () => {
      body.style.cursor = prevCursor
      body.style.userSelect = prevSelect
    }
  }, [dragging])

  const handleDragMove = useCallback(
    (clientX: number) => {
      const el = containerRef.current
      if (!el) return

      const { left, width } = el.getBoundingClientRect()
      if (width <= 0) return

      const raw = (clientX - left) / width
      // Drag past the clamp toward an edge: snap the panel shut instead of
      // leaving an awkward sliver.
      if (raw <= MIN_RATIO - SNAP_RATIO) return collapse('left')
      if (raw >= MAX_RATIO + SNAP_RATIO) return collapse('right')
      setRatio(raw)
    },
    [collapse, setRatio],
  )

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!draggable) return
      // Let the collapse buttons handle their own clicks.
      if ((e.target as HTMLElement).closest('button')) return
      e.preventDefault()
      e.currentTarget.setPointerCapture(e.pointerId)
      setDragging(true)
      dismissHint()
    },
    [draggable, dismissHint],
  )

  const onPointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!e.currentTarget.hasPointerCapture(e.pointerId)) return
      handleDragMove(e.clientX)
    },
    [handleDragMove],
  )

  const onPointerUp = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!e.currentTarget.hasPointerCapture(e.pointerId)) return
      e.currentTarget.releasePointerCapture(e.pointerId)
      setDragging(false)
      // Persist the final ratio once, instead of on every move during the drag.
      commitRatio()
    },
    [commitRatio],
  )

  // Double-click the handle resets to an even split.
  const onDoubleClick = useCallback(() => {
    if (!draggable) return
    setRatio(DEFAULT_RATIO)
    commitRatio(DEFAULT_RATIO)
    dismissHint()
  }, [draggable, setRatio, commitRatio, dismissHint])

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (!draggable) return
      let next: number
      switch (e.key) {
        case 'ArrowLeft':
          next = ratio - RATIO_STEP
          break
        case 'ArrowRight':
          next = ratio + RATIO_STEP
          break
        case 'Home':
          next = MIN_RATIO
          break
        case 'End':
          next = MAX_RATIO
          break
        default:
          return
      }
      setRatio(next)
      commitRatio(next)
      dismissHint()
      e.preventDefault()
    },
    [draggable, ratio, setRatio, commitRatio, dismissHint],
  )

  // Chevron collapse/expand also counts as discovering the handle.
  const onCollapse = useCallback(
    (side: 'left' | 'right') => {
      collapse(side)
      dismissHint()
    },
    [collapse, dismissHint],
  )
  const onExpand = useCallback(() => {
    expand()
    dismissHint()
  }, [expand, dismissHint])

  const ratioPct = Math.round(clampRatio(ratio) * 100)

  return (
    <div ref={containerRef} className={cn('flex h-full', className)}>
      <Panel
        hidden={collapsed === 'left'}
        className={leftClassName}
        // Skip the width transition while dragging so the panel tracks the
        // pointer 1:1; animate only the collapse/expand snap.
        style={collapsed ? undefined : { flex: `0 0 ${ratio * 100}%`, transition: dragging ? 'none' : undefined }}
      >
        {left}
      </Panel>

      <div
        className={cn(
          'group relative z-50 h-full px-3 focus-visible:outline-none',
          draggable && 'cursor-col-resize touch-none',
        )}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onDoubleClick={onDoubleClick}
        onKeyDown={onKeyDown}
        title={draggable ? 'Drag to resize · double-click to reset · chevrons to collapse' : undefined}
        role={draggable ? 'separator' : undefined}
        tabIndex={draggable ? 0 : undefined}
        aria-orientation={draggable ? 'vertical' : undefined}
        aria-label={draggable ? 'Resize panels' : undefined}
        aria-valuenow={draggable ? ratioPct : undefined}
        aria-valuemin={draggable ? Math.round(MIN_RATIO * 100) : undefined}
        aria-valuemax={draggable ? Math.round(MAX_RATIO * 100) : undefined}
      >
        <div
          className={cn(
            'absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-input transition-colors group-hover:bg-primary group-focus-visible:bg-primary',
            dragging && 'bg-primary',
          )}
        />
        <div
          className={cn(
            'absolute left-1/2 top-7 z-50 -translate-x-1/2 -translate-y-1/2 rounded-lg border border-transparent p-1 transition-all group-hover:border-input group-hover:bg-background group-hover:shadow-md group-focus-visible:border-input group-focus-visible:bg-background group-focus-visible:shadow-md',
            dragging && 'border-input bg-background shadow-md',
          )}
        >
          <CollapseHandle collapsed={collapsed} onCollapse={onCollapse} onExpand={onExpand} />
        </div>

        {showHint && <ResizeHint onDismiss={dismissHint} />}
      </div>

      <Panel hidden={collapsed === 'right'} className={rightClassName}>
        {right}
      </Panel>
    </div>
  )
}

// ── First-run hint ─────────────────────────────────────────────────────────

const ResizeHint = ({ onDismiss }: { onDismiss: () => void }) => (
  <div
    role='status'
    className='absolute left-1/2 top-16 z-50 flex -translate-x-1/2 items-center gap-2 whitespace-nowrap rounded-lg border border-border bg-popover px-3 py-1.5 text-xs text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95'
  >
    Drag to resize panels
    <button
      type='button'
      onClick={onDismiss}
      aria-label='Dismiss hint'
      className='-mr-1 rounded p-0.5 text-muted-foreground transition-colors hover:bg-input hover:text-foreground'
    >
      <X className='size-3.5' />
    </button>
  </div>
)

interface PanelProps {
  hidden: boolean
  className?: string
  style?: React.CSSProperties
  children: React.ReactNode
}

const Panel = ({ hidden, className, style, children }: PanelProps) => (
  <div
    className={cn('h-full overflow-auto', hidden ? 'hidden' : 'min-w-0 flex-1 transition-[flex-basis]', className)}
    style={style}
  >
    {children}
  </div>
)
